import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'

declare const $: (selector: string | HTMLElement) => any

export default class CSVUploadController extends Controller {
  static values = {
    uploadUrl: String,
    invalidFileTypeMessage: String,
    fileTooLargeMessage: String,
    maxFileSizeBytes: { type: Number, default: 10 * 1024 * 1024 }
  }

  static targets = ['csvUploadBox', 'csvUploadInput', 'fileUploads']

  declare readonly csvUploadBoxTarget: HTMLElement
  declare readonly csvUploadInputTarget: HTMLInputElement
  declare readonly hasCsvUploadInputTarget: boolean
  declare readonly fileUploadsTarget: HTMLElement
  declare readonly uploadUrlValue: string
  declare readonly invalidFileTypeMessageValue: string
  declare readonly fileTooLargeMessageValue: string
  declare readonly maxFileSizeBytesValue: number

  downloadFrame?: HTMLIFrameElement

  connect (): void {
    document.documentElement.addEventListener('dragover', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.csvUploadBoxTarget.classList.add('drag-and-drop-overlay')
    })

    document.documentElement.addEventListener('drop', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.csvUploadBoxTarget.classList.remove('drag-and-drop-overlay')
    })

    document.documentElement.addEventListener('mouseenter', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.csvUploadBoxTarget.classList.remove('drag-and-drop-overlay')
    })

    this.csvUploadBoxTarget.addEventListener('drop', (e) => {
      const file = (e as DragEvent).dataTransfer.files[0]
      this.uploadFile(file)
    })

    document.querySelector('.media-selector').addEventListener('change', (e) => {
      const file = (e.currentTarget as HTMLInputElement).files[0]
      this.uploadFile(file)
    })

    this.element.addEventListener('click', (e) => {
      const link = (e.target as HTMLElement).closest('a[download]') as HTMLAnchorElement | null
      if (!link) {
        return
      }
      // A download link to a cross-origin (S3) URL navigates the top frame, which aborts
      // the Mercure EventSource and loses live status updates. Download in a hidden iframe
      // so the page never navigates.
      e.preventDefault()
      this.downloadInIframe(link.href)
    })

    document.addEventListener('MercureEvent', (e) => {
      const data = (e as CustomEvent).detail.data
      if (data.event === 'fileUploadEvent') {
        fetch('/internal/file-upload/' + data.fileUploadId)
          .then(response => {
            if (!response.ok) {
              return Promise.reject(response)
            }

            return response.json()
          })
          .then((data) => {
            const existingRow = $('#' + data.fileUploadId)
            if (existingRow) {
              existingRow.replaceWith(data.row)
            } else {
              this.fileUploadsTarget.classList.remove('d-none')
              this.fileUploadsTarget.querySelector('tbody').insertAdjacentHTML('afterbegin', data.row)
            }
          })
      }
    })
  }

  downloadInIframe (href: string): void {
    if (!this.downloadFrame) {
      this.downloadFrame = document.createElement('iframe')
      this.downloadFrame.hidden = true
      this.element.appendChild(this.downloadFrame)
    }
    this.downloadFrame.src = href
  }

  uploadFile (file: File | null, mapping: Record<string, unknown> | null = null): void {
    if (file) {
      // The `accept=".csv"` attribute only covers the file picker, so this check is what
      // protects the drag-and-drop path.
      if (!this.isCsv(file)) {
        this.showError(this.invalidFileTypeMessageValue)
        this.clearInput()
        return
      }

      if (file.size > this.maxFileSizeBytesValue) {
        this.showError(this.fileTooLargeMessageValue)
        this.clearInput()
        return
      }

      this.hideError()

      const formData = new FormData()
      if (mapping) {
        formData.append('mapping', JSON.stringify(mapping))
      }
      formData.append('csv', file, file.name)

      fetchWithHandlingLoginRedirect(this.uploadUrlValue, {
        method: 'POST',
        body: formData
      })
        .then(response => {
          if (!response.ok) {
            return Promise.reject(response)
          }
          return response.json()
        })
        .then((result) => {
          this.fileUploadsTarget.classList.remove('d-none')
          this.fileUploadsTarget.querySelector('tbody').insertAdjacentHTML('afterbegin', result.result)
        })
    }

    this.clearInput()
  }

  isCsv (file: File): boolean {
    const name = (file.name || '').toLowerCase()

    return name.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel'
  }

  showError (message: string): void {
    const alertMessage = document.getElementById('alertMessage')
    if (alertMessage) {
      alertMessage.textContent = message
    }
    document.getElementById('alert')?.classList.remove('d-none')
  }

  hideError (): void {
    document.getElementById('alert')?.classList.add('d-none')
  }

  clearInput (): void {
    if (this.hasCsvUploadInputTarget) {
      this.csvUploadInputTarget.value = ''
    }
  }
}
